import {
  parse,
  format,
  startOfMinute,
  startOfHour,
  startOfDay,
  endOfDay,
  endOfHour,
  subDays,
  subMinutes,
  subHours,
  differenceInMinutes,
  differenceInDays,
  differenceInWeeks,
  differenceInMonths,
  differenceInYears,
  differenceInSeconds,
  lightFormat,
  isValid,
  addDays,
  sub,
  addWeeks,
  differenceInMilliseconds,
  add,
  differenceInHours,
  isAfter,
  isBefore
} from "date-fns";
import { getTimeFilter, convertHoursToFreq } from "./common";
import { defaultDateFormat, SCHEDULE_FREQUENCY, utcDateFormat } from "./enums";

/**
 * Converts short time type abbreviation to full time unit name
 * @param {string} timeType - The short time type abbreviation (y, M, w, d, h, m, s)
 * @example
 * // Input: "h"
 * // Output: "hours"
 * // Input: "d"
 * // Output: "days"
 * @returns {string} Returns the full time unit name (e.g., "hours", "days", "months"), or the original value if no mapping exists
 */
const formatTimeType = (timeType) => {
  const unitMap = {
    'y': 'years',
    'M': 'months',
    'w': 'weeks',
    'd': 'days',
    'h': 'hours',
    'm': 'minutes',
    's': 'seconds',
  };
  return unitMap[timeType] || timeType;
}

export const getCurrentTimeStamp = () => {
  let currentDateTime = new Date();

  // Extract year, month, day, hour, minute, and second components
  let year = currentDateTime.getFullYear();
  let month = String(currentDateTime.getMonth() + 1).padStart(2, '0');
  let day = String(currentDateTime.getDate()).padStart(2, '0');
  let hour = String(currentDateTime.getHours()).padStart(2, '0');
  let minute = String(currentDateTime.getMinutes()).padStart(2, '0');
  let second = String(currentDateTime.getSeconds()).padStart(2, '0');
  let ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12;
  hour = hour ? hour : 12; // Handle midnight (0 hours) as 12 AM

  return `${year}-${month}-${day} ${hour}${minute}${second} ${ampm}`;
};

export const getStartOfMinute = () => {
  return startOfMinute(new Date());
};

export const getStartOfHour = (input = new Date()) => {
  return startOfHour(input);
};

export const getStartOfDay = (input = new Date()) => {
  return startOfDay(input);
};

export const formatDate = (input) => {
  const date = new Date(input);
  return {
    utc: () => new Date(date.getTime() + date.getTimezoneOffset() * 60000)
  };
};

export const displayPathAnalyticsTime = input => {
  let localTime = "";
  if (input) {
    localTime = format(new Date(input), "MM/dd/yyyy, h:mm:ss a");
  }
  return localTime;
};

export const displayPathScoreTime = input => {
  let localTime = "";
  if (input) {
    localTime = format(new Date(input), "dd MMM yyyy h:mm a");
  }
  return localTime;
};

export const displayUserExpChartTooltp = input => {
  let localTime = "";
  if (input) {
    localTime = format(new Date(input), "dd MMM hh a");
  }
  return localTime;
};

export const shortDisplayTime = input => {
  if (!input) return "";
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return format(new Date(), "MMM dd, yyyy hh:mm a") // or return String(input)
  return format(date, "MMM dd, yyyy hh:mm a");
};

export const shortDisplayTimeOnly = input => {
  let localTime = "";
  if (input) {
    localTime = format(new Date(input), "hh:mm a");
  }
  return localTime;
};


export const shortDisplayDate = input => {
  let localTime = "";
  if (input) {
    localTime = format(new Date(input), "dd MMM yy");
  }
  return localTime;
};

export const shortDisplayDateFullYear = input => {
  let localTime = "";
  if (input) {
    localTime = format(new Date(input), "MMM dd, yyyy");
  }
  return localTime;
};

export const toDateInputValue = value => {
  const dateInputValuePattern = /^\d{4}-\d{2}-\d{2}$/;

  if (!value) return "";
  if (typeof value === "string" && dateInputValuePattern.test(value)) {
    return value;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const useUTCFields =
    typeof value === "number" ||
    (typeof value === "string" && dateInputValuePattern.test(value) === false);
  const year = useUTCFields ? date.getUTCFullYear() : date.getFullYear();
  const month = `${(useUTCFields ? date.getUTCMonth() : date.getMonth()) + 1}`.padStart(2, "0");
  const day = `${useUTCFields ? date.getUTCDate() : date.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getDateInputParts = value => {
  const DATE_INPUT_VALUE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
  if (typeof value !== "string" || !DATE_INPUT_VALUE_PATTERN.test(value)) {
    return null;
  }

  const [year, month, day] = value.split("-").map(Number);
  return { year, month, day };
};

export const getUTCStartFromDateInput = value => {
  const dateParts = getDateInputParts(value);
  if (!dateParts) {
    return getStartOfDayUTC(value);
  }

  return Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day);
};

export const getUTCEndFromDateInput = value => {
  const dateParts = getDateInputParts(value);
  if (!dateParts) {
    return getEndOfDayUTC(value);
  }

  return Date.UTC(
    dateParts.year,
    dateParts.month - 1,
    dateParts.day,
    23,
    59,
    59,
    999,
  );
};

export const addDaysToDateInputValue = (value, numberDays) => {
  const dateParts = getDateInputParts(value);
  if (!dateParts) {
    return toDateInputValue(addDays(new Date(value), numberDays));
  }

  const utcDate = new Date(
    Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day),
  );
  return toDateInputValue(addDays(utcDate, numberDays).getTime());
};

export const getDayDiffFromDateInputs = (endValue, startValue) => {
  const startTimestamp = getUTCStartFromDateInput(startValue);
  const endTimestamp = getUTCStartFromDateInput(endValue);
  return differenceInDays(new Date(endTimestamp), new Date(startTimestamp));
};

/**
 * Formats a date/time input into a short date string with year first
 * @param {Date|string|number} input - The date to format. Can be a Date object, date string, or timestamp
 * @example
 * // Input: Mon Jan 05 2026 15:22:48 GMT+0530 (India Standard Time)
 * // Output: "2026-01-05"
 * @returns {string} Returns a formatted date string in "yyyy-MM-dd" format (e.g., "2024-03-15"), or empty string if input is falsy
 */
export const shortDisplayDateYearFirst = input => {
  let localTime = "";
  if (input) {
    localTime = format(new Date(input), "yyyy-MM-dd");
  }
  return localTime;
};

/**
 * Formats a date/time input into a date string with 24-hour time format
 * @param {Date|string|number} input - The date to format. Can be a Date object, date string, or timestamp
 * @example
 * // Input: Mon Jan 05 2026 15:25:58 GMT+0530 (India Standard Time)
 * // Output: "2026-01-05 15:25"
 * @returns {string} Returns a formatted date string in "yyyy-MM-dd HH:mm" format (e.g., "2024-03-15 14:30"), or empty string if input is falsy
 */
export const shortDisplayDate24Hour = input => {
  let localTime = "";
  if (input) {
    localTime = format(new Date(input), "yyyy-MM-dd HH:mm");
  }
  return localTime;
};
export const shortDisplayDate24HourWithSec = input => {
  let localTime = "";
  if (input) {
    const date = new Date(input);
    if (!isNaN(date.getTime())) {
      localTime = lightFormat(date, utcDateFormat);
    }
  }
  return localTime;
};

export const shortDisplayDate24HourWithSecUTC = input => {
  let localTime = "";
  if (input) {
    const date = new Date(input);
    const timestampUTCDate = date.getTime() + (date.getTimezoneOffset() * 60 * 1000);
    localTime = lightFormat(new Date(timestampUTCDate), utcDateFormat);
  }
  return localTime;
};

/**
 * Extracts the hour from a date/time input in 12-hour format
 * @param {Date|string|number} input - The date to extract hour from. Can be a Date object, date string, or timestamp
 * @example
 * // Input: Mon Jan 05 2026 15:20:01 GMT+0530 (India Standard Time)
 * // Output: "3"
 * @returns {string} Returns the hour in 12-hour format (1-12) without leading zeros (e.g., "2", "11"), or empty string if input is falsy
 */
export const getHourFromDate = input => {
  let localTime = "";
  if (input) {
    localTime = format(new Date(input), "h");
  }
  return localTime;
};

/**
 * Extracts the full month name from a date/time input
 * @param {Date|string|number} input - The date to extract month from. Can be a Date object, date string, or timestamp
 * @example
 * // Input: Mon Jan 05 2026 15:29:45 GMT+0530 (India Standard Time)
 * // Output: "January"
 * @returns {string} Returns the full month name (e.g., "January", "March", "December"), or empty string if input is falsy
 */
export const displayFullMonth = input => {
  let localTime = "";
  if (input) {
    localTime = format(new Date(input), "MMMM");
  }
  return localTime;
};

/**
 * Validates whether a date/time input is a valid date
 * @param {Date|string|number} input - The date to validate. Can be a Date object, date string, or timestamp
 * @example
 * // Input: Mon Jan 05 2026 15:33:52 GMT+0530 (India Standard Time)
 * // Output: true
 * // Input: "New date"
 * // Output: false
 * @returns {boolean} Returns true if the input is a valid date, false otherwise
 */
export const isDateValid = input => {
  return isValid(new Date(input));
};

export const updateCurrTimeMinute = (input) => {
  let localTime = "";

  if (input) {
    const date = new Date(input);
    localTime = startOfMinute(date).getTime();
  }

  return localTime;
};


export const nextDay = (updatedTime) => {
  const currTime = startOfMinute(new Date(updatedTime));
  return addDays(currTime, 1).getTime();
};

export const getPeriod = (time, startType, diff, diffType) => {
  const date = new Date(time);

  // subtract time
  const subtracted = sub(date, { [diffType]: diff });

  // startOf type
  let start;
  switch (startType) {
    case "minutes":
      start = startOfMinute(subtracted);
      break;
    case "hours":
      start = startOfHour(subtracted);
      break;
    case "days":
      start = startOfDay(subtracted);
      break;
    default:
      start = subtracted; // fallback
  }

  return start.getTime();
};

export const getTimeRepeats = (repeats, currTime) => {
  let nextRun = "";

  const date = new Date(currTime);

  if (SCHEDULE_FREQUENCY.DAILY === repeats) {
    nextRun = addDays(date, 1).getTime();
  } else if (SCHEDULE_FREQUENCY.WEEKLY === repeats) {
    nextRun = addWeeks(date, 1).getTime();
  } else {
    nextRun = addDays(date, 30).getTime();
  }

  return nextRun;
};

export const getRange = time => {
  const hours = differenceInHours(
    new Date(time.current_period_end),
    new Date(time.current_period_start)
  );
  return convertHoursToFreq(hours);
};

export const getDiffBtwDates = (toDate, fromDate, type = "hours") => {
  const to = new Date(toDate);
  const from = new Date(fromDate);

  const diffFunctions = {
    years: differenceInYears,
    months: differenceInMonths,
    weeks: differenceInWeeks,
    days: differenceInDays,
    hours: differenceInHours,
    minutes: differenceInMinutes,
    seconds: differenceInSeconds,
    milliseconds: differenceInMilliseconds
  };

  const diffFunction = diffFunctions[type] || differenceInHours;
  return diffFunction(to, from);
};

export const shortDisplayDateTime = input => {
  let localTime = "";
  if (input) {
    localTime = format(new Date(input), "MM/dd/yy h a");
  }
  return localTime;
};

export const shortDisplayDateTimeHour = input => {
  let localTime = "";
  if (input) {
    localTime = format(new Date(input), "MM/dd/yy h:mm a");
  }
  return localTime;
};

export const updateTimeWithOffset = (
  createdBy,
  currPeriod,
  isDaySavingTime = true
) => {
  const _createdBy = createdBy === "ui" ? "ui_0_0" : createdBy; //i.e._createdBy could be 'ui_420' or 'ui_-330'
  const offsetStart = parseInt(_createdBy.split("_")[1]);
  const offsetEnd = parseInt(_createdBy.split("_")[2]);
  const offsetDifference = offsetEnd - offsetStart;
  const positiveOffset = offsetDifference > 0 ? 3600 * 1000 : -3600 * 1000; //offset is 420
  const negativeOffset = offsetDifference < 0 ? +3600 * 1000 : -3600 * 1000; //offset is -120

  if (isDaySavingTime || offsetDifference === 0) {
    return new Date(
      offsetStart > 0
        ? currPeriod - offsetStart * 60 * 1000
        : currPeriod + Math.abs(offsetStart) * 60 * 1000
    ).toString();
  }

  return new Date(
    offsetStart > 0
      ? currPeriod - offsetStart * 60 * 1000 - positiveOffset
      : currPeriod + Math.abs(offsetStart) * 60 * 1000 + negativeOffset //i.e 1647212400000 + 120*60*1000+3600000
  ).toString();
};


export const isDateInBetween = (compareDate, fromDate, toDate) => {
  const date = new Date(compareDate.valueOf());
  const start = new Date(fromDate);
  const end = new Date(toDate);
  const result = isAfter(date, start) && isBefore(date, end);
  return result;
};

export const getRangeFourHours = input => {
  let localTime = "";
  if (input) {
    localTime = subHours(new Date(input), 4);
  }
  return localTime;
};


/**
 * Converts a date/time input into a Date object
 * @param {Date|string|number} input - The date to convert. Can be a Date object, date string, or timestamp
 * @example
 * // Input: "2026-01-05"
 * // Output: "Mon Jan 05 2026 05:30:00 GMT+0530"
 * @returns {Date|string} Returns a Date object if input is provided, or empty string if input is falsy
 */
export const currentDateFormat = (input) => {
  let localTime = "";

  if (input) {
    localTime = new Date(input);
  }
  return localTime;
};

/**
 * Subtracts specified time units from a date
 * @param {Date|string|number} input - The date to subtract from. Can be a Date object, date string, or timestamp
 * @param {number} numberUnits - The number of units to subtract
 * @param {string} typeTime - The time unit type (e.g., "days", "hours", "minutes", "years")
 * @example
 * // Input: Mon Jan 05 2026 18:11:47 GMT+0530, 5, "days"
 * // Output: Date object for Wed Dec 31 2025 18:11:47 GMT+0530
 * @returns {Date|string} Returns a Date object with the subtracted time if input is provided, or empty string if input is falsy
 */
export const subtractUnitsFromDate = (input, numberUnits, typeTime) => {
  let localTime = "";

  if (input) {
    localTime = sub(new Date(input), {
      [formatTimeType(typeTime)]: numberUnits
    });
  }
  return localTime;
};

/**
 * Adds specified time units to a date
 * @param {Date|string|number} input - The date to add to. Can be a Date object, date string, or timestamp
 * @param {number} numberUnits - The number of units to add
 * @param {string} typeTime - The time unit type abbreviation (y, M, w, d, h, m, s) or full name (e.g., "days", "hours")
 * @example
 * // Input: Mon Jan 05 2026 18:17:15 GMT+0530, 5, "d"
 * // Output: Date object for Sat Jan 10 2026 18:17:15 GMT+0530
 * @returns {Date|string} Returns a Date object with the added time if input is provided, or empty string if input is falsy
 */
export const addUnitsFromDate = (input, numberUnits, typeTime) => {
  let localTime = "";

  if (input) {
    const newTimeType = formatTimeType(typeTime);
    localTime = add(new Date(input), { [newTimeType]: numberUnits });
  }
  return localTime;
};

/**
 * Calculates the duration in hours between two dates
 * @param {Date|string|number} max - The end date. Can be a Date object, date string, or timestamp
 * @param {Date|string|number} min - The start date. Can be a Date object, date string, or timestamp
 * @example
 * // Input: new Date('2026-01-05T14:00:00'), new Date('2026-01-05T12:00:00')
 * // Output: 2 (hours)
 * @returns {number|string} Returns the duration in hours as a number if both dates are provided, or empty string if either date is missing
 */
export const getRangeDurationAsHours = (max, min) => {
  let hours = "";
  if (max && min) {
    const diffInMs = differenceInMilliseconds(new Date(max), new Date(min));
    hours = diffInMs / (1000 * 60 * 60); // Convert milliseconds to hours
  }
  return hours;
};

export const getArrayMinValue = input => {
  let min = "";
  if (input) {
    min = Math.min(...input);
  }
  return min;
};

export const getArrayMaxValue = input => {
  let max = "";
  if (input) {
    max = Math.max(...input);
  }
  return max;
};

export const getArrayAvgValue = input => {
  let avg = "";
  if (input) {
    avg = input.reduce((a, b) => a + b, 0) / input.length;
  }
  return avg;
};

/**
 * Description: Returns the given date as a string using given date format
 * @param {Date | number} date is a Date object / timestamp
 * @param {string} strFormat is the format of the final date string
 * @returns {string} date string in the given date string format
 */
export const displayDateTime = (date, strFormat = defaultDateFormat) => {
  return date ? format(date, strFormat) : "";
};

export const displayDateTimeSeconds = (date) => {
  const utcDate = new Date(date);       // Create Date object in UTC

  const localDate = utcDate.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  return localDate;
};

export const prevDay = (input = new Date()) => {
  const now = new Date(input);
  const localOffset = now.getTimezoneOffset(); // Negative for ahead of UTC
  const prevDayTimestamp = subDays(now, 1).getTime();

  if (localOffset < 0) { // Ahead of UTC (negative offset)
    // Get UTC start of day for the previous day
    const prevDayDate = new Date(prevDayTimestamp);
    const utcTime = Date.UTC(
      prevDayDate.getUTCFullYear(),
      prevDayDate.getUTCMonth(),
      prevDayDate.getUTCDate()
    );
    return utcTime;
  }
  return prevDayTimestamp;
}

export const getTimePeriodELT = (time, seed) => {
  const timeFilter = getTimeFilter(time);
  let current_period = [];
  let previous_period = [];
  let inputTime = seed ? seed : new Date();
  // floor it to previous 5th min
  const minute = new Date().getMinutes();
  const fifthMinute = Math.floor(minute / 5) * 5;
  const currTime = shortDisplayDateTimeHour(subMinutes(new Date(inputTime), minute - fifthMinute));
  const currentValue = new Date(currTime).valueOf();

  switch (timeFilter) {
    case "1": {
      const oneHourAgo = startOfHour(subHours(new Date(currTime), 1));
      current_period = [
        oneHourAgo.valueOf(),
        currentValue,
      ];
      const twoHoursAgo = startOfHour(subHours(new Date(currTime), 2));
      previous_period = [
        twoHoursAgo.valueOf(),
        oneHourAgo.valueOf()
      ];
      return { current_period, previous_period };
    }
    case "3": {
      current_period = [
        startOfHour(subHours(new Date(currTime), 3)).valueOf(),
        currentValue,
      ];
      previous_period = [
        startOfHour(subHours(new Date(currTime), 6)).valueOf(),
        subMinutes(new Date(currTime), 181).valueOf()
      ];
      return { current_period, previous_period };
    }
    case "6": {
      current_period = [
        startOfHour(subHours(new Date(currTime), 6)).valueOf(),
        currentValue,
      ];
      previous_period = [
        startOfHour(subHours(new Date(currTime), 12)).valueOf(),
        subMinutes(new Date(currTime), 361).valueOf()
      ];
      return { current_period, previous_period };
    }
    case "24": {
      current_period = [
        startOfHour(subHours(new Date(currTime), 24)).valueOf(),
        currentValue,
      ];
      previous_period = [
        startOfHour(subHours(new Date(currTime), 48)).valueOf(),
        subMinutes(new Date(currTime), 1441).valueOf()
      ];
      return { current_period, previous_period };
    }
    case "168": {
      current_period = [
        startOfHour(startOfDay(subDays(new Date(currTime), 7))).valueOf(),
        currentValue,
      ];
      previous_period = [
        startOfHour(startOfDay(subDays(new Date(currTime), 14))).valueOf(),
        subMinutes(startOfDay(new Date(currTime)), 10081).valueOf()
      ];
      return { current_period, previous_period };
    }
    case "720": {
      current_period = [
        startOfHour(startOfDay(subDays(new Date(currTime), 30))).valueOf(),
        currentValue,
      ];
      previous_period = [
        startOfHour(startOfDay(subDays(new Date(currTime), 60))).valueOf(),
        subMinutes(startOfDay(new Date(currTime)), 43201).valueOf()
      ];
      return { current_period, previous_period };
    }
    default: {//case for 12 hours
      current_period = [
        startOfHour(subHours(new Date(currTime), 12)).valueOf(),
        currentValue,
      ];
      previous_period = [
        startOfHour(subHours(new Date(currTime), 24)).valueOf(),
        subMinutes(new Date(currTime), 721).valueOf()
      ];
      return { current_period, previous_period };
    }
  }
};

/**
 * Calculates the timestamp of a time period start by subtracting specified hours from a given time
 * @param {Date|string|number} time - The reference time. Can be a Date object, date string, or timestamp
 * @param {number} diff - The number of hours to subtract from the time
 * @example
 * // Input: new Date('2024-03-15T14:00:00'), 3
 * // Output: 1710493200000 (timestamp for 2024-03-15T11:00:00)
 * @returns {number} Returns the timestamp in milliseconds of the time period start
 */
export const getTimePeriodStartHour = (time, diff) => {
  return subHours(new Date(time), diff).getTime();
}

export const getEndOfHour = (input = new Date()) => {
  return endOfHour(new Date(input));
};

/**
 * Description: This method converts date hour number to timestamp
 * @param {*} dateHour is a date time number in YYYYMMDDHH format
 */

export const convertDateHourToTimestamp = dateHour => {
  if (dateHour !== null) {
    const dateHourStr = dateHour.toString();
    const year = dateHourStr.slice(0, 4);
    const month = parseInt(dateHourStr.slice(4, 6)) - 1;
    0, 1;
    const date = dateHourStr.slice(6, 8);
    const hour = dateHourStr.slice(8, 10);
    const min = dateHourStr.slice(10, 12);
    return Date.UTC(year, month, date, hour, min);
  }
};

/**
 * Description: This method converts date number to timestamp
 * @param {*} date is a date time number in YYYYMMDD format
 */

export const convertDateToTimestamp = dateNumber => {
  const dateStr = dateNumber.toString();
  const year = dateStr.slice(0, 4);
  const month = parseInt(dateStr.slice(4, 6)) - 1;
  0, 1;
  const date = dateStr.slice(6, 8);
  return Date.UTC(year, month, date);
};

/**
 * Description: This method returns beginning time of UTC date
 * @param {number | Date | string} startDate is the start date
 * @returns {number} beginning time of UTC date in milliseconds
 */
export const getStartOfDayUTC = startDate => {
  const date = new Date(startDate);
  return Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
};

/**
 * Description: This method ending time of UTC date
 * @param {number | Date | string} endDate is the end date
 * @returns {number} ending time of UTC date in milliseconds
 */
export const getEndOfDayUTC = endDate => {
  const date = new Date(endDate);
  return Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999
  );
};

export const getStartfDayUTCFormat = (dateStr) => {
  const date = new Date(dateStr);
  // Convert to UTC by adjusting timezone offset
  const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  return format(utcDate, "MMM dd, yyyy hh:mm a");

};

export const convertTsToLocal = (timestamp) => {
  let date = timestamp + " GMT"
  return new Date(date);
};

/**
 * Description: Returns the given date as a sring using given utc date format
 * @param {Date | number} date is a Date object / timestamp
 * @param {string} format is the format of the final date string
 * @returns utc date string in the given format
 */
export const getFormattedUTCDate = (date, format = "yyyy-MM-dd HH:mm:ss") => {
  if (date) {
    const localeDate = new Date(date);
    const timestampUTCDate =
      localeDate.getTime() + (localeDate.getTimezoneOffset() * 60 * 1000);
    return lightFormat(timestampUTCDate, format);
  } else return "";
};

/**
 * Description: Returns the timestamp of the given UTC date string using the given date format
 * @param {string} date is a UTC date string
 * @param {string} format is the format of the given UTC date string without time zone
 * @returns the timestamp of the given UTC date string
 */
export const getTimestampFromUTCString = (
  date,
  format = "yyyy-MM-dd HH:mm:ssX"
) => {
  return parse(`${date}Z`, format, new Date()).getTime();
};

/**
 * Description: Returns the given UTC date string in the new date string format
 * @param {string} date is a date string
 * @param {string} strFormat is the format of the final date string
 * @param {string} sourceFormat is the format of the given UTC date string without time zone
 * @returns {string} date string in the new date string format
 */
export const getFormattedDateFromUTCString = (
  date,
  strFormat = "MMM dd, yyyy hh:mm aa",
  sourceFormat = "yyyy-MM-dd HH:mm:ssX"
) => {
  return format(parse(`${date}Z`, sourceFormat, new Date()), strFormat);
};

/**
 * Description: Returns the given date string in the new date string format
 * @param {string} date is a date string
 * @param {string} sourceFormat is the format of the source date string
 * @param {string} strFormat is the format of the final date string
 * @returns {string} date string in the new date string format
 */
export const getFormattedDateFromString = (
  date,
  sourceFormat = "MM/dd/yyyy HH:mm",
  strFormat = "MMM dd, yyyy hh:mm aa"
) => {
  return format(parse(date, sourceFormat, new Date()), strFormat);
};

/**
 * Description: Returns the custom time filter range
 * @param {string[]} range is the dates
 * @returns {object} the custom time filter range
 */
export const getCustomTimeFilterRange = (range = [null, null], maxHistoricalRangeDays = 30) => {

  const date = new Date();
  const result = {
    minDate: subDays(new Date(date), maxHistoricalRangeDays),
    maxDate: endOfDay(subDays(new Date(date), 1)),
    error: false
  };

  if (range[0]) {
    const startDate = new Date(range[0]);

    if (range[1]) {
      const endDate = new Date(range[1]);
      const diffInDays = differenceInDays(endDate, startDate);
      const diffInMinutes = differenceInMinutes(endDate, startDate);

      if (diffInDays > 6 || diffInMinutes === 0) result.error = true;
    }
  }

  return result;
};

/**
 * Description: Converts the given timestamps to local time based on the provided time range type (timeZone).
 * @param {number} currentPeriodStart - The start timestamp of the current period.
 * @param {number} currentPeriodEnd - The end timestamp of the current period.
 * @param {string} timeRangeType - The time zone to use for formatting.
 * @returns {object} An object containing the formatted start time, end time, and time zone abbreviation.
 */
export const convertTimestampToLocalTime = (currentPeriodStart, currentPeriodEnd, timeRangeType) => {
  let timeZone = timeRangeType;

  // Convert "normal" time zone to "UTC" to support legacy vmanage reports
  if (timeZone === "normal") {
    timeZone = "UTC";
  }

  const formatDate = (timestamp, timeZone) => {
    const date = new Date(timestamp);
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timeZone  // Use the provided time zone
    };
    return new Intl.DateTimeFormat('en-US', options).format(date);
  };

  // Format the start and end times using the formatDate function and the provided time zone
  const startTimeFormatted = formatDate(currentPeriodStart, timeZone);
  const endTimeFormatted = formatDate(currentPeriodEnd, timeZone);

  // Extracts the time zone abbreviation (e.g., "PST") from the timestamp.
  const timeZoneAbbr = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'short',
    timeZone: timeZone,
  }).format(new Date(currentPeriodStart)).split(' ').pop();

  // Return an object containing the formatted start time, end time, and time zone abbreviation
  return { startTimeFormatted, endTimeFormatted, timeZoneAbbr };

};

/**
 * Description: From the current date to 7 days ago
 * @returns {array} returns start and end timestamp
 */
export const backtoSevenDays = () => {
  const timestamp = Date.now();
  const msMinutes5 = 5 * 60 * 1000;
  const msDays6 = 6 * 24 * 60 * 60 * 1000;
  const timePeriod = [
    new Date(timestamp - msDays6).setHours(0, 0, 0, 0),
    timestamp - timestamp % msMinutes5
  ]
  return timePeriod;
}

export const epochToLocal = (epochTime) => {
  const date = new Date(epochTime);
  return date.toLocaleString();
}

export const convertTimestampToDate = (timestamp) => {
  return new Date(timestamp);
}

/**
 * Parses time string in various formats and returns a valid Date object
 * Supports: ISO strings, Date objects, '08:46 AM' (12-hour), '08:46' (24-hour)
 * @param {string|Date} timeString - Time value to parse
 * @returns {Date} - Valid Date object
 */
export const parseTimeToDate = (timeString) => {
  if (!timeString) {
    return new Date();
  }

  // If it's already a Date object
  if (timeString instanceof Date) {
    return timeString;
  }

  try {
    // Try parsing as ISO string or standard date format
    const date = new Date(timeString);
    if (!isNaN(date.getTime())) {
      return date;
    }

    // If it's in '08:46 AM' format (12-hour with AM/PM)
    if (typeof timeString === 'string' && /\d{1,2}:\d{2}\s?(AM|PM)/i.test(timeString)) {
      return parse(timeString, "hh:mm a", new Date());
    }

    // If it's in '08:46' format (24-hour)
    if (typeof timeString === 'string' && /^\d{1,2}:\d{2}$/.test(timeString)) {
      return parse(timeString, "HH:mm", new Date());
    }

    // Fallback to current time
    return new Date();
  } catch (error) {
    console.error("Error parsing time string:", error);
    return new Date();
  }
};
